import { useEffect, useMemo, useState } from 'react';

import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea
} from '@workspace/ui';

import {
  SHARE_SCOPE_OPTIONS,
  formatShareScopeLabel,
  formatTenantIds,
  isPortalAssetReadOnly,
  normalizeShareScope,
  parseTenantIds,
  type CanonicalShareScope,
  type SharedMappingAsset
} from '@/lib/mapping-assets';

export interface TenantShare {
  tenant_id: string;
  created_at?: string;
  created_by?: string | null;
}

interface MappingAssetAccessSheetProps<TAsset extends SharedMappingAsset> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetTypeLabel: string;
  asset: TAsset | null;
  featureTenantId?: string;
  loadShares: (assetId: string) => Promise<TenantShare[]>;
  saveAccess: (asset: TAsset, input: { shareScope: CanonicalShareScope; tenantIds: string[] }) => Promise<void>;
}

export function MappingAssetAccessSheet<TAsset extends SharedMappingAsset>({
  open,
  onOpenChange,
  assetTypeLabel = 'Asset',
  asset,
  featureTenantId,
  loadShares,
  saveAccess
}: MappingAssetAccessSheetProps<TAsset>) {
  const [shareScope, setShareScope] = useState<CanonicalShareScope>('tenant');
  const [tenantIdsText, setTenantIdsText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isReadOnly = useMemo(
    () => isPortalAssetReadOnly(asset?.tenant_id, featureTenantId),
    [asset?.tenant_id, featureTenantId]
  );

  useEffect(() => {
    if (!open || !asset?.id) {
      return;
    }

    let cancelled = false;
    setError(null);
    setShareScope(normalizeShareScope(asset.share_scope));
    setTenantIdsText('');
    setLoading(true);

    loadShares(asset.id)
      .then(shares => {
        if (cancelled) {
          return;
        }
        setTenantIdsText(formatTenantIds(shares.map(share => share.tenant_id)));
      })
      .catch(err => {
        if (cancelled) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to load shared tenants';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [asset?.id, asset?.share_scope, loadShares, open]);

  const handleSave = async () => {
    if (!asset) {
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await saveAccess(asset, {
        shareScope,
        tenantIds: shareScope === 'shared' ? parseTenantIds(tenantIdsText) : []
      });
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save access';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="lg">
        <SheetHeader>
          <SheetTitle>{asset ? `${asset.name ?? asset.id} access` : `${assetTypeLabel} access`}</SheetTitle>
          <SheetDescription>
            Review sharing for this {assetTypeLabel.toLowerCase()}. Shared and platform-owned assets are read-only in the portal.
          </SheetDescription>
        </SheetHeader>

        {asset ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg border border-border bg-muted/20 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="capitalize">
                  {formatShareScopeLabel(asset.share_scope)}
                </Badge>
                <Badge variant={isReadOnly ? 'outline' : 'secondary'}>
                  {isReadOnly ? 'Read-only' : 'Owned by current tenant'}
                </Badge>
              </div>
              <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                <div>ID: {asset.id ?? '—'}</div>
                <div>Owning tenant: {asset.tenant_id ?? 'platform'}</div>
                <div>Updated: {asset.updated_at ?? '—'}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-share-scope">Share scope</Label>
              <Select value={shareScope} onValueChange={value => setShareScope(value as CanonicalShareScope)} disabled={isReadOnly}>
                <SelectTrigger id="asset-share-scope">
                  <SelectValue placeholder="Select share scope" />
                </SelectTrigger>
                <SelectContent>
                  {SHARE_SCOPE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {SHARE_SCOPE_OPTIONS.find(option => option.value === shareScope)?.description}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-share-tenants">Shared tenant IDs</Label>
              <Textarea
                id="asset-share-tenants"
                value={tenantIdsText}
                onChange={event => setTenantIdsText(event.target.value)}
                className="min-h-[180px] font-mono text-xs"
                placeholder="tenant-a\ntenant-b"
                disabled={isReadOnly || loading || shareScope !== 'shared'}
              />
              <p className="text-xs text-muted-foreground">
                One tenant ID per line. This list is applied only when the share scope is set to shared.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-owner-tenant">Current tenant context</Label>
              <Input id="asset-owner-tenant" value={featureTenantId ?? 'No active tenant'} readOnly />
            </div>

            {error ? (
              <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {!isReadOnly ? (
                <Button onClick={handleSave} disabled={saving || loading}>
                  {saving ? 'Saving…' : 'Save access'}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
